import React, { useMemo } from 'react'
import {
    DataTableCellRenderer,
    DataTableColumn,
    DataTableFiltersState,
    DataTableNestedPanel,
    DataTableOpenPanelButton,
    getNoMatchesText,
    NestedDataTable,
    NestedDataTableState,
    useDataTableFilters,
} from '../DataTable'
import { FilterDefinition, SearchValuesByColumnId } from '../../utils/tableFilters'

type LinkedRecordsPanelProps<TRecord extends { _id: string }, TColumnId extends string> = {
    ownerName: string
    ownerLabel: string
    recordsQuery: { isLoading: boolean; isError: boolean }
    records: TRecord[]
    linkedIds: string[] | undefined
    recordDisplayName: { singular: string; plural: string }
    columns: DataTableColumn<TRecord, TColumnId>[]
    filterDefinitions: FilterDefinition<TRecord>[]
    getSearchValues: (record: TRecord) => SearchValuesByColumnId
    renderCell: DataTableCellRenderer<TRecord, TColumnId>
    getRecordName: (record: TRecord) => string
    getRecordPath: (record: TRecord) => string | null
    filtersState: DataTableFiltersState
    state: NestedDataTableState<TColumnId>
}

export const LinkedRecordsPanel = <TRecord extends { _id: string }, TColumnId extends string>({
    ownerName,
    ownerLabel,
    recordsQuery,
    records,
    linkedIds,
    recordDisplayName,
    columns,
    filterDefinitions,
    getSearchValues,
    renderCell,
    getRecordName,
    getRecordPath,
    filtersState,
    state,
}: LinkedRecordsPanelProps<TRecord, TColumnId>) => {
    const recordsLabel = recordDisplayName.plural.toLowerCase()

    const linkedRecords = useMemo(() => {
        const linkedIdSet = new Set(linkedIds)
        return records.filter((record) => linkedIdSet.has(record._id))
    }, [records, linkedIds])

    const { toolbarProps, hiddenColumnIds, hasActiveFilters, visibleRows } = useDataTableFilters({
        rows: linkedRecords,
        filterDefinitions,
        getSearchValues,
        state: filtersState,
    })

    return (
        <DataTableNestedPanel
            isLoading={recordsQuery.isLoading}
            isError={recordsQuery.isError}
            errorText={`Unable to load the ${recordsLabel} linked to ${ownerName}.`}
        >
            <NestedDataTable
                columns={columns}
                rows={visibleRows}
                getRowId={(record) => record._id}
                renderCell={renderCell}
                hiddenColumnIds={hiddenColumnIds}
                renderRowActions={(record) => {
                    const recordPath = getRecordPath(record)

                    return (
                        <DataTableOpenPanelButton
                            accessibleLabel={`Open ${getRecordName(record)}`}
                            to={recordPath ?? undefined}
                            disabled={!recordPath}
                        />
                    )
                }}
                state={state}
                toolbarProps={{ ...toolbarProps, filterMenuWidth: 260 }}
                searchPlaceholder={`${recordDisplayName.singular} Search`}
                emptyText={
                    hasActiveFilters
                        ? getNoMatchesText(recordsLabel)
                        : `No ${recordsLabel} are linked to this ${ownerLabel.toLowerCase()} yet.`
                }
            />
        </DataTableNestedPanel>
    )
}
